/**
 * Template (category) page script — the PDF preview modal, ratings, the
 * ChatGPT Q&A box, the form preview + prefill hand-off, the guest download
 * popup, the Trustpilot prompt and the in-page search suggestions.
 *
 * Until Sep 16 2026 these ~118 KB lived inline in categories/show.blade.php
 * on every template page view. Page values arrive through the
 * #category-page-config JSON block; functions that inline onclick handlers in
 * the Blade call are exported on window at the bottom.
 */
const configElement = document.getElementById('category-page-config');
let cfg = {};
try {
    cfg = JSON.parse(configElement ? configElement.textContent : '{}') || {};
} catch (error) {
    cfg = {};
}
cfg.form = cfg.form || {};
cfg.chatgpt = cfg.chatgpt || {};

document.addEventListener('DOMContentLoaded', function() {
        const categoryDownloadIncrementUrl = cfg.incrementUrl;
        // Read at request time: partials/csrf-refresh swaps the token after load on cached pages.
        const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

        function incrementCategoryDownloadCount() {
            const payload = new FormData();
            payload.append('_token', csrfToken());

            if (navigator.sendBeacon) {
                try {
                    if (navigator.sendBeacon(categoryDownloadIncrementUrl, payload)) {
                        return;
                    }
                } catch (error) {
                    // Fall through to fetch below.
                }
            }

            fetch(categoryDownloadIncrementUrl, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': csrfToken(),
                    'Accept': 'application/json',
                },
                body: payload,
                keepalive: true,
            }).catch(error => {
                console.error('Error incrementing download count:', error);
            });
        }

        (function initH2CascadeSections() {
            const documentContent = document.getElementById('document-content');
            if (!documentContent) return;
            if (documentContent.dataset.collapseH2Sections !== '1') return;
            if (documentContent.querySelector('.h2-cascade-section')) return;

            const childNodes = Array.from(documentContent.childNodes);
            const topLevelH2Count = childNodes.filter((node) => node.nodeType === Node.ELEMENT_NODE && node.tagName === 'H2').length;
            if (topLevelH2Count === 0) return;

            const fragment = document.createDocumentFragment();
            let sectionIndex = 0;
            let currentSectionContent = null;

            childNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'H2') {
                    const section = document.createElement('section');
                    section.className = 'h2-cascade-section';

                    const heading = document.createElement('h2');
                    heading.className = 'h2-cascade-heading';

                    const trigger = document.createElement('button');
                    trigger.type = 'button';
                    trigger.className = 'h2-cascade-trigger';
                    const isFirstSection = sectionIndex === 0;
                    trigger.setAttribute('aria-expanded', isFirstSection ? 'true' : 'false');

                    const headingId = node.id || `doc-h2-heading-${sectionIndex}`;
                    heading.id = headingId;

                    if (node.getAttribute('style')) {
                        heading.setAttribute('style', node.getAttribute('style'));
                    }

                    if (node.className) {
                        heading.classList.add(...node.className.split(/\s+/).filter(Boolean));
                    }

                    const label = document.createElement('span');
                    label.className = 'h2-cascade-label';
                    label.innerHTML = node.innerHTML;

                    const content = document.createElement('div');
                    content.className = isFirstSection ? 'h2-cascade-content' : 'h2-cascade-content hidden';
                    content.id = `doc-h2-content-${sectionIndex}`;
                    content.setAttribute('role', 'region');
                    content.setAttribute('aria-labelledby', heading.id);

                    trigger.setAttribute('aria-controls', content.id);

                    const icon = document.createElement('span');
                    icon.className = 'h2-cascade-icon';
                    icon.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>';

                    trigger.appendChild(label);
                    trigger.appendChild(icon);
                    heading.appendChild(trigger);

                    trigger.addEventListener('click', function() {
                        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
                        trigger.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
                        content.classList.toggle('hidden', isExpanded);
                    });

                    section.appendChild(heading);
                    section.appendChild(content);
                    fragment.appendChild(section);

                    currentSectionContent = content;
                    sectionIndex++;
                    return;
                }

                if (currentSectionContent) {
                    currentSectionContent.appendChild(node);
                    return;
                }

                fragment.appendChild(node);
            });

            documentContent.innerHTML = '';
            documentContent.appendChild(fragment);
        })();

        // Table of Contents functionality with collapsible sections
        (function initTableOfContents() {
            const tocContainer = document.getElementById('toc-container');
            const tocNav = document.getElementById('toc-nav');
            // Find all content columns (there may be multiple due to mobile reordering)
            const mainContentColumns = document.querySelectorAll('.lg\\:col-span-2');

            if (!tocContainer || !tocNav || mainContentColumns.length === 0) return;

            // Find all headings in ALL main content columns (left side sections)
            let allHeadings = [];
            mainContentColumns.forEach(col => {
                allHeadings = allHeadings.concat(Array.from(col.querySelectorAll('h1, h2, h3')));
            });

            // Filter out headings we don't want in TOC
            const headings = Array.from(allHeadings).filter(heading => {
                const text = heading.textContent.trim().toLowerCase();
                if (!text || text.length === 0) return false;
                if (heading.closest('.hidden') || heading.closest('[style*="display: none"]')) return false;
                if (heading.closest('#pdf-modal') || heading.closest('#rating-modal')) return false;
                return true;
            });

            if (headings.length < 2) return;

            // Show the TOC container
            tocContainer.classList.remove('hidden');

            // Helper function to title case text (capitalize first letter of each word, lowercase rest)
            function toTitleCase(str) {
                return str.toLowerCase().replace(/(?:^|\s)\S/g, function(char) {
                    return char.toUpperCase();
                });
            }

            // Helper function to check if text starts with a numbered/roman numeral pattern
            function isNumberedHeading(text) {
                // Match patterns like: "I.", "II.", "III.", "IV.", "1.", "2.", "Step 1", etc.
                const numberedPattern = /^(([ivxlcdm]+\.)|(\d+\.)|(\d+\))|step\s+\d+)/i;
                return numberedPattern.test(text.trim());
            }

            // Build hierarchical structure
            const tocStructure = [];
            let currentParent = null;

            headings.forEach((heading, index) => {
                if (!heading.id) {
                    heading.id = 'toc-heading-' + index;
                }

                const level = parseInt(heading.tagName.charAt(1));
                let text = heading.textContent.trim();
                const originalText = text;
                text = toTitleCase(text);
                if (text.length > 45) {
                    text = text.substring(0, 42) + '...';
                }

                const item = {
                    heading: heading,
                    headingId: heading.id,
                    text: text,
                    level: level,
                    children: [],
                    isNumbered: isNumberedHeading(originalText)
                };

                // Determine if this should be a child or parent
                if (level === 1) {
                    // H1 is always top level and can be a parent
                    tocStructure.push(item);
                    currentParent = item;
                } else if (level === 2) {
                    // H2 with roman numerals/numbers should be child of previous non-numbered heading
                    if (item.isNumbered && currentParent && !currentParent.isNumbered) {
                        currentParent.children.push(item);
                    } else {
                        // Non-numbered H2 is a top level item and can be a parent
                        tocStructure.push(item);
                        if (!item.isNumbered) {
                            currentParent = item;
                        }
                    }
                } else if (level === 3) {
                    // H3 - try to add as child of current parent
                    if (currentParent) {
                        currentParent.children.push(item);
                    } else {
                        tocStructure.push(item);
                    }
                } else {
                    tocStructure.push(item);
                }
            });

            // Render TOC with collapsible sections
            const tocItems = [];

            tocStructure.forEach((item, index) => {
                const hasChildren = item.children.length > 0;

                // Create wrapper for parent item
                const wrapper = document.createElement('div');
                wrapper.className = 'toc-section';

                // Create parent item
                const parentItem = document.createElement('div');
                parentItem.className = 'toc-parent flex items-center justify-between';

                const parentLink = document.createElement('a');
                parentLink.href = '#' + item.headingId;
                parentLink.className = 'toc-item flex-1 block py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 border-l-2 border-transparent hover:border-gray-400 pl-3';
                parentLink.setAttribute('data-heading-id', item.headingId);
                if (item.level === 1) {
                    parentLink.classList.add('font-semibold');
                } else {
                    parentLink.classList.add('font-medium');
                }
                parentLink.textContent = item.text;

                // Smooth scroll on click
                parentLink.addEventListener('click', function(e) {
                    e.preventDefault();
                    const targetHeading = document.getElementById(item.headingId);
                    if (targetHeading) {
                        const headerOffset = 100;
                        const elementPosition = targetHeading.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                });

                parentItem.appendChild(parentLink);
                tocItems.push({ element: parentLink, heading: item.heading, headingId: item.headingId, level: item.level, wrapper: wrapper });

                // Add expand/collapse button if has children
                if (hasChildren) {
                    const toggleBtn = document.createElement('button');
                    toggleBtn.className = 'toc-toggle p-1 text-gray-400 hover:text-gray-600 transition-colors';
                    toggleBtn.setAttribute('aria-label', 'Show or hide this section');
                    toggleBtn.innerHTML = '<svg class="w-4 h-4 transform transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>';
                    toggleBtn.setAttribute('aria-expanded', 'false');
                    parentItem.appendChild(toggleBtn);

                    // Create children container (hidden by default)
                    const childrenContainer = document.createElement('div');
                    childrenContainer.className = 'toc-children hidden pl-2';

                    // Add count indicator to parent
                    const countSpan = document.createElement('span');
                    countSpan.className = 'text-xs text-gray-400 ml-1';
                    countSpan.textContent = `(${item.children.length})`;
                    parentLink.appendChild(countSpan);

                    item.children.forEach(child => {
                        const childLink = document.createElement('a');
                        childLink.href = '#' + child.headingId;
                        childLink.className = 'toc-item block py-1 text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200 border-l-2 border-transparent hover:border-gray-400 pl-5';
                        childLink.setAttribute('data-heading-id', child.headingId);
                        childLink.textContent = child.text;

                        childLink.addEventListener('click', function(e) {
                            e.preventDefault();
                            const targetHeading = document.getElementById(child.headingId);
                            if (targetHeading) {
                                const headerOffset = 100;
                                const elementPosition = targetHeading.getBoundingClientRect().top;
                                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                            }
                        });

                        childrenContainer.appendChild(childLink);
                        tocItems.push({ element: childLink, heading: child.heading, headingId: child.headingId, level: child.level, parent: wrapper });
                    });

                    // Toggle children visibility
                    toggleBtn.addEventListener('click', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
                        toggleBtn.setAttribute('aria-expanded', !isExpanded);
                        toggleBtn.querySelector('svg').classList.toggle('rotate-90', !isExpanded);
                        childrenContainer.classList.toggle('hidden', isExpanded);
                    });

                    wrapper.appendChild(parentItem);
                    wrapper.appendChild(childrenContainer);
                    wrapper.tocToggle = toggleBtn;
                    wrapper.tocChildren = childrenContainer;
                } else {
                    wrapper.appendChild(parentItem);
                }

                tocNav.appendChild(wrapper);
            });

            // Scroll spy with auto-expand/collapse
            let currentExpandedWrapper = null;

            function updateActiveHeading() {
                const scrollTop = window.scrollY + 150;
                let activeItem = null;

                for (let i = 0; i < tocItems.length; i++) {
                    const item = tocItems[i];
                    const rect = item.heading.getBoundingClientRect();
                    const headingTop = rect.top + window.scrollY;

                    if (headingTop <= scrollTop) {
                        activeItem = item;
                    } else {
                        break;
                    }
                }

                if (!activeItem && tocItems.length > 0) {
                    const firstHeading = tocItems[0].heading;
                    const rect = firstHeading.getBoundingClientRect();
                    if (rect.top < window.innerHeight) {
                        activeItem = tocItems[0];
                    }
                }

                // Remove active class from all
                tocItems.forEach(item => {
                    item.element.classList.remove('text-gray-900', 'border-gray-900');
                    item.element.classList.add('text-gray-600', 'border-transparent');
                    if (item.level === 3) {
                        item.element.classList.remove('text-gray-900');
                        item.element.classList.add('text-gray-500');
                    }
                });

                // Determine which wrapper should be expanded
                let targetWrapper = null;
                if (activeItem) {
                    // If active item is a child (h3), its parent wrapper should be expanded
                    if (activeItem.parent) {
                        targetWrapper = activeItem.parent;
                    }
                    // If active item is a parent with children, it should be expanded
                    else if (activeItem.wrapper && activeItem.wrapper.tocToggle) {
                        targetWrapper = activeItem.wrapper;
                    }
                }

                // Collapse previous wrapper if different from target
                if (currentExpandedWrapper && currentExpandedWrapper !== targetWrapper) {
                    if (currentExpandedWrapper.tocToggle) {
                        currentExpandedWrapper.tocToggle.setAttribute('aria-expanded', 'false');
                        currentExpandedWrapper.tocToggle.querySelector('svg').classList.remove('rotate-90');
                        currentExpandedWrapper.tocChildren.classList.add('hidden');
                    }
                }

                // Expand target wrapper
                if (targetWrapper && targetWrapper.tocToggle) {
                    targetWrapper.tocToggle.setAttribute('aria-expanded', 'true');
                    targetWrapper.tocToggle.querySelector('svg').classList.add('rotate-90');
                    targetWrapper.tocChildren.classList.remove('hidden');
                }

                currentExpandedWrapper = targetWrapper;

                // Add active class
                if (activeItem) {
                    activeItem.element.classList.remove('text-gray-600', 'border-transparent', 'text-gray-500');
                    activeItem.element.classList.add('text-gray-900', 'border-gray-900');
                }
            }

            // Throttle scroll event
            let scrollTimeout;
            window.addEventListener('scroll', function() {
                if (scrollTimeout) {
                    window.cancelAnimationFrame(scrollTimeout);
                }
                scrollTimeout = window.requestAnimationFrame(updateActiveHeading);
            });

            setTimeout(updateActiveHeading, 100);
        })();

        // Sticky Create Header functionality
        (function initStickyHeader() {
            const stickyHeader = document.getElementById('sticky-create-header');
            const tocContainer = document.getElementById('toc-container');

            if (!stickyHeader) return;

            function getVisibleAboutCard() {
                return Array.from(document.querySelectorAll('.about-card'))
                    .find((card) => card.offsetParent !== null);
            }

            function updateStickyHeader() {
                // Use the visible PDF preview + buttons card as the trigger point.
                const triggerElement = getVisibleAboutCard() || tocContainer;
                if (!triggerElement) return;

                const triggerRect = triggerElement.getBoundingClientRect();
                const triggerBottom = triggerRect.bottom;

                // Show sticky header when the trigger element is scrolled out of view
                if (triggerBottom < 0) {
                    stickyHeader.style.transform = 'translateY(0)';
                } else {
                    stickyHeader.style.transform = 'translateY(-100%)';
                }
            }

            // Throttle scroll event
            let scrollTimeout;
            window.addEventListener('scroll', function() {
                if (scrollTimeout) {
                    window.cancelAnimationFrame(scrollTimeout);
                }
                scrollTimeout = window.requestAnimationFrame(updateStickyHeader);
            });

            // Initial check
            updateStickyHeader();
        })();

        // PDF Modal functionality
        const pdfModal = document.getElementById('pdf-modal');
        const closeModal = document.getElementById('close-modal');
        const previewModalBtn = document.getElementById('preview-modal-btn');
        const pdfPreviewTriggers = document.querySelectorAll('[data-pdf-preview-trigger]');
        
        function openModal() {
            const pdfIframe = document.getElementById('pdf-iframe');
            if (pdfIframe && !pdfIframe.getAttribute('src') && pdfIframe.dataset.src) {
                pdfIframe.setAttribute('src', pdfIframe.dataset.src);
            }
            pdfModal.classList.remove('hidden');
            // Mark that the PDF was viewed
            sessionStorage.setItem(('pdf_viewed_' + cfg.id), 'true');
        }

        function closeModalFunc() {
            pdfModal.classList.add('hidden');

            // Check if user has already rated this category in this session
            const hasRated = sessionStorage.getItem(('rated_' + cfg.id));
            const hasViewed = sessionStorage.getItem(('pdf_viewed_' + cfg.id));

            // Show rating modal only if they viewed the PDF and haven't rated yet
            if (hasViewed && !hasRated) {
                setTimeout(function() {
                    document.getElementById('rating-modal').classList.remove('hidden');
                }, 500); // Small delay for better UX
            }
        }
        
        if (previewModalBtn) {
            previewModalBtn.addEventListener('click', openModal);
        }
        
        pdfPreviewTriggers.forEach((trigger) => {
            trigger.addEventListener('click', openModal);
        });
        
        if (closeModal) {
            closeModal.addEventListener('click', closeModalFunc);
        }
        
        // Close modal when clicking outside
        if (pdfModal) {
            pdfModal.addEventListener('click', function(event) {
                if (event.target === pdfModal || event.target.classList.contains('absolute')) {
                    closeModalFunc();
                }
            });
        }

        // Rating Modal functionality
        const ratingModal = document.getElementById('rating-modal');
        const closeRatingModal = document.getElementById('close-rating-modal');
        const starBtns = document.querySelectorAll('.star-btn');
        const ratingThanks = document.getElementById('rating-thanks');

        // Close rating modal
        function closeRatingModalFunc() {
            ratingModal.classList.add('hidden');
            // Reset stars
            starBtns.forEach(btn => {
                btn.classList.remove('text-yellow-400');
                btn.classList.add('text-gray-700');
            });
            ratingThanks.classList.add('hidden');
        }

        if (closeRatingModal) {
            closeRatingModal.addEventListener('click', closeRatingModalFunc);
        }

        // Close rating modal when clicking outside
        if (ratingModal) {
            ratingModal.addEventListener('click', function(event) {
                if (event.target === ratingModal || event.target.classList.contains('backdrop-blur-sm')) {
                    closeRatingModalFunc();
                }
            });
        }

        // Handle star rating clicks
        starBtns.forEach(btn => {
            btn.addEventListener('click', function() {
                const rating = parseInt(this.getAttribute('data-rating'));

                // Update star colors
                starBtns.forEach((star, index) => {
                    if (index < rating) {
                        star.classList.remove('text-gray-700');
                        star.classList.add('text-yellow-400');
                    } else {
                        star.classList.add('text-gray-700');
                        star.classList.remove('text-yellow-400');
                    }
                });

                // Submit rating to server
                submitRating(rating);
            });
        });

        function submitRating(rating) {
            // Mark as rated in session storage
            sessionStorage.setItem(('rated_' + cfg.id), 'true');

            // Send to server
            fetch(cfg.ratingStoreUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    category_id: cfg.id,
                    rating: rating
                })
            })
            .then(response => response.json())
            .then(data => {
                // Show thank you message
                ratingThanks.classList.remove('hidden');

                // Update rating display on the page
                updateRatingDisplay();

                // Close modal after 2 seconds
                setTimeout(function() {
                    closeRatingModalFunc();
                }, 2000);
            })
            .catch(error => {
                console.error('Error submitting rating:', error);
                // Still close the modal even if there's an error
                setTimeout(function() {
                    closeRatingModalFunc();
                }, 2000);
            });
        }

        function updateRatingDisplay() {
            // Fetch updated rating stats
            fetch(cfg.ratingStatsUrl)
                .then(response => response.json())
                .then(data => {
                    const ratingDisplay = document.getElementById('rating-display');

                    if (data.total > 0) {
                        if (ratingDisplay) {
                            // Update existing display
                            document.getElementById('avg-rating').textContent = data.average;
                            document.getElementById('total-ratings').textContent = data.total;
                        } else {
                            // Create new rating display if it didn't exist
                            const downloadDiv = document.querySelector('.flex.items-center.text-gray-700:has(svg path[d*="M12 10v6"])');
                            if (downloadDiv && downloadDiv.parentElement) {
                                const newRatingDiv = document.createElement('div');
                                newRatingDiv.id = 'rating-display';
                                newRatingDiv.className = 'flex items-center text-gray-700';
                                newRatingDiv.innerHTML = `
                                    <svg class="w-4 h-4 mr-1.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                    </svg>
                                    <span class="text-sm"><span id="avg-rating">${data.average}</span> (<span id="total-ratings">${data.total}</span> ${data.total == 1 ? 'rating' : 'ratings'})</span>
                                `;
                                downloadDiv.parentElement.insertBefore(newRatingDiv, downloadDiv);
                            }
                        }
                    }
                })
                .catch(error => console.error('Error fetching rating stats:', error));
        }

        // Show ChatGPT Q&A Box in its position (above "Not the form you're looking for?" section)
        const chatgptContainer = document.getElementById('chatgpt-qa-container');

        if (chatgptContainer) {
            // Simply show the container in its current position
            chatgptContainer.classList.remove('hidden');
        }
        
        // ChatGPT Q&A Functionality
        const chatForm = document.getElementById('chatgpt-form');
        const chatQuestion = document.getElementById('chatgpt-question');
        const charCount = document.getElementById('char-count');
        const chatSubmit = document.getElementById('chatgpt-submit');
        const exampleQuestionsContainer = document.getElementById('example-questions');
        let thinkingMessageElement = null; // Track the thinking message
        
        // Store AI form data for CTA button
        const aiFormData = {
            hasForm: cfg.form.hasForm,
            url: cfg.form.url,
            categoryName: cfg.name,
            useAn: cfg.form.useAn
        };
        
        // Load example questions
        async function loadExampleQuestions() {
            const container = document.getElementById('example-questions-container');
            const loadingEl = document.getElementById('example-questions-loading');
            const categoryId = cfg.id;
            
            try {
                const response = await fetch(`${cfg.chatgpt.exampleQuestionsUrl}?category_id=${categoryId}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });
                
                const data = await response.json();
                
                // Clear loading state
                if (loadingEl) {
                    loadingEl.remove();
                }
                
                // Add questions
                if (data.questions && data.questions.length > 0) {
                    data.questions.forEach(question => {
                        const button = document.createElement('button');
                        button.type = 'button';
                        button.className = 'example-question text-xs px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors duration-200 text-left border border-gray-200';
                        button.textContent = question;
                        
                        // Add click event to populate the question field
                        button.addEventListener('click', function() {
                            chatQuestion.value = this.textContent.trim();
                            charCount.textContent = chatQuestion.value.length;
                            chatQuestion.focus();
                        });
                        
                        container.appendChild(button);
                    });
                }
            } catch (error) {
                console.error('Error loading example questions:', error);
                
                // Clear loading state
                if (loadingEl) {
                    loadingEl.remove();
                }
                
                // Add fallback questions
                const fallbackQuestions = [
                    'What makes it legally binding?',
                    'Do I need a witness?',
                    'What information is required?'
                ];
                
                fallbackQuestions.forEach(question => {
                    const button = document.createElement('button');
                    button.type = 'button';
                    button.className = 'example-question text-xs px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors duration-200 text-left border border-gray-200';
                    button.textContent = question;
                    
                    button.addEventListener('click', function() {
                        chatQuestion.value = this.textContent.trim();
                        charCount.textContent = chatQuestion.value.length;
                        chatQuestion.focus();
                    });
                    
                    container.appendChild(button);
                });
            }
        }
        
        // Load example questions when ChatGPT container is visible
        if (chatgptContainer) {
            loadExampleQuestions();
        }
        
        // Update character count
        if (chatQuestion) {
            chatQuestion.addEventListener('input', function() {
                charCount.textContent = this.value.length;
            });
        }
        
        // Add message to chat
        function addMessage(content, isUser = false, showCTA = false) {
            // Ensure chat messages container still exists
            const messagesContainer = document.getElementById('chat-messages');
            if (!messagesContainer) {
                console.error('Chat messages container not found');
                return;
            }
            
            const messageDiv = document.createElement('div');
            messageDiv.className = `flex ${isUser ? 'justify-end' : 'justify-start'} message-item`;
            
            const messageContent = document.createElement('div');
            messageContent.className = `max-w-[80%] rounded-lg p-3 ${
                isUser 
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-gray-900' 
                    : 'bg-gray-50 text-gray-800 border border-gray-200'
            }`;
            
            if (!isUser) {
                // Add AI label for bot messages
                const label = document.createElement('div');
                label.className = 'text-xs text-gray-500 mb-1 flex items-center gap-1';
                label.innerHTML = '<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path></svg> AI Assistant';
                messageContent.appendChild(label);
            }
            
            const text = document.createElement('div');
            text.innerHTML = content; // Use innerHTML to support markdown formatting
            text.className = 'text-sm leading-relaxed';
            messageContent.appendChild(text);
            
            messageDiv.appendChild(messageContent);
            messagesContainer.appendChild(messageDiv);
            
            // Add CTA button after AI responses if form exists
            if (!isUser && showCTA && aiFormData.hasForm) {
                const ctaDiv = document.createElement('div');
                ctaDiv.className = 'flex justify-start mt-3 message-item';
                
                const ctaContainer = document.createElement('div');
                ctaContainer.className = 'max-w-[80%]';
                
                const ctaButton = document.createElement('a');
                ctaButton.href = aiFormData.url;
                ctaButton.className = 'inline-flex items-center px-3 py-1.5 bg-black text-white font-medium rounded-lg hover:bg-gray-900 focus:outline-none transition-all duration-200 shadow-md text-xs increment-download-count border-2 border-black';
                ctaButton.innerHTML = `
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                    </svg>
                    Create ${aiFormData.categoryName}
                `;
                
                // Add download tracking
                ctaButton.addEventListener('click', function() {
                    incrementCategoryDownloadCount();
                });
                
                ctaContainer.appendChild(ctaButton);
                ctaDiv.appendChild(ctaContainer);
                messagesContainer.appendChild(ctaDiv);
            }
            
            // Show messages container if hidden
            if (messagesContainer.classList.contains('hidden')) {
                messagesContainer.classList.remove('hidden');
            }
            
            // Scroll to bottom
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
            
            // Ensure message stays visible (failsafe)
            setTimeout(() => {
                if (!messageDiv.parentNode && messagesContainer) {
                    console.warn('Message was removed, re-adding it');
                    messagesContainer.appendChild(messageDiv);
                }
            }, 100);
        }
        
        // Format AI response
        function formatResponse(text) {
            // Convert line breaks to <br> tags
            text = text.replace(/\n/g, '<br>');
            // Convert **bold** to <strong>
            text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            // Convert *italic* to <em>
            text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
            return text;
        }
        
        // Show thinking indicator
        function showThinkingIndicator() {
            const messagesContainer = document.getElementById('chat-messages');
            if (!messagesContainer) {
                console.error('Chat messages container not found');
                return;
            }
            
            const messageDiv = document.createElement('div');
            messageDiv.className = 'flex justify-start thinking-message message-item';
            
            const messageContent = document.createElement('div');
            messageContent.className = 'max-w-[80%] rounded-lg p-3 bg-gray-50 text-gray-800 border border-gray-200 thinking-indicator';
            
            // Add AI label
            const label = document.createElement('div');
            label.className = 'text-xs text-gray-500 mb-1 flex items-center gap-1';
            label.innerHTML = '<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path></svg> AI Assistant';
            messageContent.appendChild(label);
            
            // Add animated thinking text
            const thinkingDiv = document.createElement('div');
            thinkingDiv.className = 'flex items-center gap-2';
            thinkingDiv.innerHTML = `
                <span class="text-sm text-gray-700">Thinking</span>
                <div class="flex gap-1 ml-1">
                    <span class="animate-bounce inline-block w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full shadow-lg" style="animation-delay: 0ms;"></span>
                    <span class="animate-bounce inline-block w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full shadow-lg" style="animation-delay: 200ms;"></span>
                    <span class="animate-bounce inline-block w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full shadow-lg" style="animation-delay: 400ms;"></span>
                </div>
            `;
            messageContent.appendChild(thinkingDiv);
            
            messageDiv.appendChild(messageContent);
            messagesContainer.appendChild(messageDiv);
            
            // Show messages container if hidden
            if (messagesContainer.classList.contains('hidden')) {
                messagesContainer.classList.remove('hidden');
            }
            
            // Scroll to bottom
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
            
            // Store reference to remove later
            thinkingMessageElement = messageDiv;
        }
        
        // Remove thinking indicator
        function removeThinkingIndicator() {
            if (thinkingMessageElement && thinkingMessageElement.parentNode) {
                // Only remove if it's still in the DOM
                thinkingMessageElement.remove();
                thinkingMessageElement = null;
            }
        }
        
        // Handle form submission
        if (chatForm) {
            chatForm.addEventListener('submit', async function(e) {
                e.preventDefault();
                
                const question = chatQuestion.value.trim();
                if (!question) return;
                
                // Disable form
                chatQuestion.disabled = true;
                chatSubmit.disabled = true;
                
                // Hide example questions after first question
                if (exampleQuestionsContainer && !exampleQuestionsContainer.classList.contains('hidden')) {
                    exampleQuestionsContainer.classList.add('hidden');
                }
                
                // Add user message
                addMessage(question, true);
                
                // Clear input
                chatQuestion.value = '';
                charCount.textContent = '0';
                
                // Show thinking indicator after user message
                showThinkingIndicator();
                
                try {
                    const response = await fetch(cfg.chatgpt.askUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                        },
                        body: JSON.stringify({
                            question: question,
                            category_id: cfg.id
                        })
                    });
                    
                    const data = await response.json();
                    
                    // Remove thinking indicator before adding response
                    removeThinkingIndicator();
                    
                    // Small delay to ensure DOM updates properly
                    await new Promise(resolve => setTimeout(resolve, 50));
                    
                    if (data.success) {
                        // Add AI response with CTA
                        addMessage(formatResponse(data.answer), false, true);
                    } else {
                        addMessage('I apologize, but I encountered an error processing your question. Please try again later.', false, false);
                    }
                    
                } catch (error) {
                    console.error('ChatGPT Error:', error);
                    // Remove thinking indicator on error
                    removeThinkingIndicator();
                    addMessage('I apologize, but I couldn\'t connect to the server. Please check your internet connection and try again.', false, false);
                } finally {
                    // Re-enable form
                    chatQuestion.disabled = false;
                    chatSubmit.disabled = false;
                    chatQuestion.focus();
                }
            });
        }
        
        // Handle download count increment for Create buttons.
        const incrementButtons = document.querySelectorAll('.increment-download-count');
        incrementButtons.forEach(button => {
            button.addEventListener('click', function(e) {
                incrementCategoryDownloadCount();
            });
        });
        
        const previewStep = document.getElementById('first-form-step');
        const previewFieldContainers = previewStep ? Array.from(previewStep.querySelectorAll('[data-preview-field]')) : [];
        const previewConditionalLogic = (() => {
            const conditionalLogicElement = document.getElementById('preview-step-conditional-logic');
            if (!conditionalLogicElement) {
                return [];
            }

            try {
                const parsed = JSON.parse(conditionalLogicElement.textContent);
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                return [];
            }
        })();

        const escapeAttributeValue = (value) => String(value)
            .replace(/\\/g, '\\\\')
            .replace(/"/g, '\\"');

        const normalizePreviewIndex = (value) => {
            if (value === null || value === undefined || value === '' || value === 'null') {
                return null;
            }

            const parsedValue = parseInt(value, 10);
            return Number.isFinite(parsedValue) ? parsedValue : null;
        };

        const readPreviewFieldAliases = (fieldContainer) => {
            try {
                const parsed = JSON.parse(fieldContainer.dataset.fieldAliases || '[]');
                return Array.isArray(parsed) ? parsed.map((alias) => String(alias)) : [];
            } catch (error) {
                return [];
            }
        };

        const previewFieldDefinitions = previewFieldContainers.map((fieldContainer, index) => {
            const aliases = new Set(readPreviewFieldAliases(fieldContainer));
            const key = fieldContainer.dataset.fieldKey || `field_${index}`;
            const fieldId = fieldContainer.dataset.fieldId;
            const fieldName = fieldContainer.dataset.fieldName;

            [key, fieldId, fieldName].filter(Boolean).forEach((alias) => aliases.add(String(alias)));

            return {
                container: fieldContainer,
                key,
                aliases: Array.from(aliases),
                additionalIndex: normalizePreviewIndex(fieldContainer.dataset.fieldAdditionalIndex),
                fieldType: fieldContainer.dataset.fieldType || '',
            };
        });

        const hasValue = (value) => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }

            return String(value ?? '').trim() !== '';
        };

        const isPreviewFieldVisible = (fieldContainer) =>
            fieldContainer.style.display !== 'none' && fieldContainer.offsetParent !== null;

        const focusPreviewField = (fieldContainer) => {
            const focusTarget = fieldContainer.querySelector('input, select, textarea');
            if (!focusTarget) {
                return;
            }

            focusTarget.focus();
            focusTarget.classList.add('border-red-500');

            setTimeout(() => {
                focusTarget.classList.remove('border-red-500');
            }, 3000);
        };

        const getPreviewFieldValue = (fieldContainer) => {
            const fieldKey = fieldContainer.dataset.fieldKey || '';
            const fieldType = fieldContainer.dataset.fieldType || '';
            const safeFieldKey = escapeAttributeValue(fieldKey);

            const checkedRadio = fieldContainer.querySelector(`input[type="radio"][name="${safeFieldKey}"]:checked`);
            if (fieldType === 'radio') {
                return checkedRadio ? checkedRadio.value : '';
            }

            const checkedBoxes = fieldContainer.querySelectorAll(
                `input[type="checkbox"][name="${safeFieldKey}[]"]:checked, input[type="checkbox"][name="${safeFieldKey}"]:checked`
            );
            if (fieldType === 'checkbox') {
                return Array.from(checkedBoxes).map((checkbox) => checkbox.value);
            }

            const control = fieldContainer.querySelector(
                `textarea[name="${safeFieldKey}"], select[name="${safeFieldKey}"], input[name="${safeFieldKey}"]`
            );

            return control ? control.value : '';
        };

        const clearPreviewFieldValue = (fieldContainer) => {
            fieldContainer.querySelectorAll('input, select, textarea').forEach((input) => {
                if (input.type === 'radio' || input.type === 'checkbox') {
                    input.checked = false;
                    return;
                }

                input.value = '';
            });
        };

        const resolvePreviewFieldReference = (fieldRef, fieldIndex = null, additionalIndex = null) => {
            const normalizedAdditionalIndex = normalizePreviewIndex(additionalIndex);
            const normalizedFieldIndex = normalizePreviewIndex(fieldIndex);

            if (fieldRef !== null && fieldRef !== undefined && fieldRef !== '') {
                const normalizedRef = String(fieldRef);
                const matchedField = previewFieldDefinitions.find((fieldDefinition) =>
                    fieldDefinition.aliases.includes(normalizedRef)
                );

                if (matchedField) {
                    return matchedField;
                }
            }

            if (normalizedFieldIndex === 0) {
                if (normalizedAdditionalIndex !== null) {
                    return previewFieldDefinitions.find((fieldDefinition) => fieldDefinition.additionalIndex === normalizedAdditionalIndex) || null;
                }

                return previewFieldDefinitions.find((fieldDefinition) => fieldDefinition.additionalIndex === null) || null;
            }

            return null;
        };

        const getPreviewFieldAliases = (fieldRef, fieldIndex = null, additionalIndex = null) => {
            const aliases = new Set();

            if (fieldRef !== null && fieldRef !== undefined && fieldRef !== '') {
                aliases.add(String(fieldRef));
            }

            const resolvedField = resolvePreviewFieldReference(fieldRef, fieldIndex, additionalIndex);
            if (resolvedField) {
                resolvedField.aliases.forEach((alias) => aliases.add(String(alias)));
            }

            return Array.from(aliases);
        };

        const getPreviewFieldValueByAliases = (aliases) => {
            for (const alias of aliases) {
                const matchedField = previewFieldDefinitions.find((fieldDefinition) =>
                    fieldDefinition.aliases.includes(String(alias))
                );

                if (matchedField) {
                    return getPreviewFieldValue(matchedField.container);
                }
            }

            return undefined;
        };

        const evaluatePreviewCondition = (value, condition, targetValue) => {
            const isEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0);

            switch (condition) {
                case 'equals':
                    return value == targetValue;
                case 'not_equals':
                    return !isEmpty && value != targetValue;
                case 'contains':
                    return value && value.toString().toLowerCase().includes(String(targetValue).toLowerCase());
                case 'not_contains':
                    return !value || !value.toString().toLowerCase().includes(String(targetValue).toLowerCase());
                case 'is_empty':
                    return isEmpty;
                case 'is_not_empty':
                    return !isEmpty;
                default:
                    return false;
            }
        };

        const ruleTargetsPreviewField = (rule, fieldDefinition) => {
            const fieldAliases = new Set(fieldDefinition.aliases);
            const targetFields = Array.isArray(rule.target_fields) ? rule.target_fields : [];
            const targetIndices = Array.isArray(rule.target_field_indices) ? rule.target_field_indices : [];
            const targetAdditionalIndices = Array.isArray(rule.target_field_additional_indices) ? rule.target_field_additional_indices : [];

            for (let index = 0; index < targetFields.length; index += 1) {
                const targetAliases = getPreviewFieldAliases(
                    targetFields[index],
                    targetIndices[index] ?? null,
                    targetAdditionalIndices[index] ?? null
                );

                if (targetAliases.some((targetAlias) => fieldAliases.has(String(targetAlias)))) {
                    return true;
                }
            }

            return false;
        };

        const shouldShowPreviewField = (rule) => {
            const evaluateSingleCondition = (condition) => {
                const parentRef = condition.parent_field_id || condition.parent_field || null;
                const parentAliases = getPreviewFieldAliases(
                    parentRef,
                    condition.parent_field_index ?? null,
                    condition.parent_field_additional_index ?? null
                );
                const parentValue = getPreviewFieldValueByAliases(parentAliases);
                const operator = condition.condition || condition.operator || 'equals';

                return evaluatePreviewCondition(parentValue, operator, condition.value);
            };

            let conditionMet = false;
            if (rule.conditions && Array.isArray(rule.conditions)) {
                const conditionResults = rule.conditions.map(evaluateSingleCondition);
                conditionMet = (rule.logic_operator || 'AND') === 'AND'
                    ? conditionResults.every(Boolean)
                    : conditionResults.some(Boolean);
            } else {
                const parentRef = rule.parent_field_id || rule.parent_field || null;
                const parentAliases = getPreviewFieldAliases(
                    parentRef,
                    rule.parent_field_index ?? null,
                    rule.parent_field_additional_index ?? null
                );
                const parentValue = getPreviewFieldValueByAliases(parentAliases);
                const operator = rule.condition || rule.operator || 'equals';

                conditionMet = evaluatePreviewCondition(parentValue, operator, rule.value);
            }

            if (rule.action === 'hide') {
                return !conditionMet;
            }

            return conditionMet;
        };

        const updatePreviewConditionalVisibility = () => {
            if (previewFieldDefinitions.length === 0 || previewConditionalLogic.length === 0) {
                return;
            }

            previewFieldDefinitions.forEach((fieldDefinition) => {
                if (fieldDefinition.additionalIndex === null) {
                    return;
                }

                const conditionalRule = previewConditionalLogic.find((rule) => ruleTargetsPreviewField(rule, fieldDefinition));
                if (!conditionalRule) {
                    fieldDefinition.container.style.display = '';
                    return;
                }

                const shouldShow = shouldShowPreviewField(conditionalRule);
                if (shouldShow) {
                    fieldDefinition.container.style.display = '';
                    return;
                }

                fieldDefinition.container.style.display = 'none';
                clearPreviewFieldValue(fieldDefinition.container);
            });
        };

        if (previewFieldContainers.length > 0 && previewConditionalLogic.length > 0) {
            previewStep.querySelectorAll('input, select, textarea').forEach((input) => {
                if (input.dataset.previewConditionalListenerSet === 'true') {
                    return;
                }

                input.addEventListener('change', updatePreviewConditionalVisibility);
                input.addEventListener('input', updatePreviewConditionalVisibility);
                input.dataset.previewConditionalListenerSet = 'true';
            });

            updatePreviewConditionalVisibility();
        }

        // Handle Next button in form preview
        const formPreviewNext = document.getElementById('preview-next-button');
        if (formPreviewNext) {
            formPreviewNext.addEventListener('click', function(event) {
                if (cfg.form.url) {
                    const previewFields = previewFieldContainers.filter(isPreviewFieldVisible);

                    if (previewFields.length === 0) {
                        incrementCategoryDownloadCount();
                        return;
                    }

                    event.preventDefault();

                    const stepOneFieldValues = {};
                    const firstFieldKey = previewFieldDefinitions[0]?.key || 'field_0';
                    let invalidField = null;

                    previewFields.forEach((fieldContainer, index) => {
                        const fieldKey = fieldContainer.dataset.fieldKey || `field_${index}`;
                        const isRequired = fieldContainer.dataset.fieldRequired === 'true';
                        const fieldValue = getPreviewFieldValue(fieldContainer);

                        if (hasValue(fieldValue)) {
                            stepOneFieldValues[fieldKey] = fieldValue;
                        }

                        if (!invalidField && isRequired && !hasValue(fieldValue)) {
                            invalidField = fieldContainer;
                        }
                    });

                    if (invalidField) {
                        alert('Please complete each required field before continuing.');
                        focusPreviewField(invalidField);
                        return;
                    }

                    const formData = {
                        step1: {
                            name: firstFieldKey || 'field_0',
                            value: stepOneFieldValues[firstFieldKey] ?? '',
                            fields: stepOneFieldValues,
                            completed: false
                        },
                        currentStep: 0
                    };

                    sessionStorage.setItem('aiFormProgress', JSON.stringify(formData));

                    incrementCategoryDownloadCount();

                    const url = new URL(cfg.form.url, window.location.origin);
                    url.searchParams.set('prefill', '1');
                    // Keep Google Ads attribution: the layout's gclid link
                    // interceptor skips clicks we've already handled.
                    window.location.href = typeof window.appendGclid === 'function'
                        ? window.appendGclid(url.toString())
                        : url.toString();
                }
            });
        }
        
        // Tab functionality for ChatGPT Q&A
        window.switchTab = function(tabName) {
            const askTab = document.getElementById('ask-question-tab');
            const recentTab = document.getElementById('recent-questions-tab');
            const askContent = document.getElementById('ask-question-content');
            const recentContent = document.getElementById('recent-questions-content');
            
            if (tabName === 'ask-question') {
                // Switch to Ask Question tab
                askTab.classList.add('active', 'text-gray-900', 'border-gray-900');
                askTab.classList.remove('text-gray-600', 'border-transparent');
                recentTab.classList.remove('active', 'text-gray-900', 'border-gray-900');
                recentTab.classList.add('text-gray-600', 'border-transparent');
                
                askContent.classList.remove('hidden');
                askContent.classList.add('active');
                recentContent.classList.add('hidden');
                recentContent.classList.remove('active');
            } else if (tabName === 'recent-questions') {
                // Switch to Recent Questions tab
                recentTab.classList.add('active', 'text-gray-900', 'border-gray-900');
                recentTab.classList.remove('text-gray-600', 'border-transparent');
                askTab.classList.remove('active', 'text-gray-900', 'border-gray-900');
                askTab.classList.add('text-gray-600', 'border-transparent');
                
                recentContent.classList.remove('hidden');
                recentContent.classList.add('active');
                askContent.classList.add('hidden');
                askContent.classList.remove('active');
                
                // Load recent questions if not already loaded
                if (!recentContent.getAttribute('data-loaded')) {
                    loadRecentQuestions();
                    recentContent.setAttribute('data-loaded', 'true');
                }
            }
        };
        
        // Authentication state
        const isAuthenticated = cfg.isAuthenticated;
        
        // Load recent questions
        async function loadRecentQuestions(sort = 'recent') {
            const container = document.getElementById('chatgpt-qa-container');
            const categoryId = container ? container.getAttribute('data-category-id') : null;
            
            if (!categoryId) {
                console.error('Category ID not found');
                return;
            }
            
            const loadingEl = document.getElementById('recent-questions-loading');
            const listEl = document.getElementById('recent-questions-list');
            const emptyEl = document.getElementById('recent-questions-empty');
            const countEl = document.getElementById('recent-questions-count');
            
            // Show loading state
            loadingEl.classList.remove('hidden');
            listEl.classList.add('hidden');
            emptyEl.classList.add('hidden');
            
            try {
                const response = await fetch(`${cfg.chatgpt.recentQuestionsUrl}?category_id=${categoryId}&limit=10&sort=${sort}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });
                
                const data = await response.json();
                
                if (data.success && data.questions.length > 0) {
                    // Hide loading and empty states
                    loadingEl.classList.add('hidden');
                    emptyEl.classList.add('hidden');
                    
                    // Show count in tab
                    if (data.total_count > 0) {
                        countEl.textContent = data.total_count > 99 ? '99+' : data.total_count;
                        countEl.classList.remove('hidden');
                    }
                    
                    // Build questions HTML
                    let questionsHtml = '';
                    data.questions.forEach(q => {
                        const helpfulClass = q.user_vote === 'helpful' ? 'text-green-500' : 'text-gray-600 hover:text-green-400';
                        const notHelpfulClass = q.user_vote === 'not_helpful' ? 'text-red-500' : 'text-gray-600 hover:text-red-400';
                        
                        questionsHtml += `
                            <div class="bg-gray-50/50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors duration-200">
                                <div class="flex items-start gap-3">
                                    <div class="flex-shrink-0 mt-1">
                                        <div class="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center border border-gray-300">
                                            <svg class="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <h4 class="text-sm font-medium text-gray-800 mb-1" style="font-family: \'Merriweather\', Georgia, serif;">${escapeHtml(q.question)}</h4>
                                        <div id="answer-${q.id}" class="text-sm text-gray-600 line-clamp-3 mb-2">${formatResponse(q.answer)}</div>
                                        <div class="flex items-center justify-between gap-2">
                                            <span class="text-xs text-gray-500 truncate">${q.created_at}</span>
                                            <button type="button"
                                                    class="text-xs text-gray-900 hover:text-gray-700 transition-colors duration-200 whitespace-nowrap"
                                                    onclick="expandAnswer(this, ${q.id})">
                                                Read more
                                            </button>
                                        </div>
                                        <div id="answer-disclaimer-${q.id}" class="hidden mt-3 pt-3 border-t border-gray-200">
                                            <div class="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                                                <p class="text-xs text-yellow-400 flex items-start gap-2">
                                                    <svg class="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                                                    </svg>
                                                    This information is for educational purposes only and does not constitute legal advice. Please consult with a qualified attorney for advice specific to your situation.
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <!-- Voting and Comment Section -->
                                        <div class="mt-4 pt-4 border-t border-gray-200">
                                            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                                    <span class="text-xs text-gray-500">Was this helpful?</span>
                                                    <div class="flex items-center gap-2">
                                                        <button type="button" 
                                                                class="vote-btn flex items-center gap-1 px-2 py-1 rounded transition-colors duration-200 ${helpfulClass}"
                                                                onclick="voteInteraction(${q.id}, true)"
                                                                data-interaction-id="${q.id}"
                                                                data-vote-type="helpful">
                                                            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z"></path>
                                                            </svg>
                                                            <span class="text-xs helpful-count">${q.helpful_count}</span>
                                                        </button>
                                                        <button type="button" 
                                                                class="vote-btn flex items-center gap-1 px-2 py-1 rounded transition-colors duration-200 ${notHelpfulClass}"
                                                                onclick="voteInteraction(${q.id}, false)"
                                                                data-interaction-id="${q.id}"
                                                                data-vote-type="not-helpful">
                                                            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z"></path>
                                                            </svg>
                                                            <span class="text-xs not-helpful-count">${q.not_helpful_count}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <button type="button" 
                                                        class="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-700 transition-colors duration-200 self-start sm:self-auto"
                                                        onclick="toggleComments(${q.id})">
                                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                                                    </svg>
                                                    <span class="comments-count">${q.comments_count}</span> <span class="hidden sm:inline">Comments</span>
                                                </button>
                                            </div>
                                            
                                            <!-- Comments Section (hidden by default) -->
                                            <div id="comments-${q.id}" class="hidden mt-4 space-y-3">
                                                <div id="comments-list-${q.id}" class="space-y-2">
                                                    <!-- Comments will be loaded here -->
                                                </div>
                                                <form onsubmit="addComment(event, ${q.id})" class="${isAuthenticated ? 'w-full' : 'space-y-2'}">
                                                    ${!isAuthenticated ? `
                                                        <div class="flex flex-col gap-1.5">
                                                            <input type="text"
                                                                   name="guest_name"
                                                                   placeholder="Your name"
                                                                   class="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                                                                   maxlength="100"
                                                                   required>
                                                            <input type="email"
                                                                   name="guest_email"
                                                                   placeholder="Your email (optional)"
                                                                   class="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                                                                   maxlength="255">
                                                        </div>
                                                    ` : ''}
                                                    <div class="flex items-start gap-2">
                                                        <textarea
                                                               name="comment"
                                                               placeholder="Add a comment..."
                                                               class="flex-1 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 resize-none"
                                                               maxlength="500"
                                                               rows="2"
                                                               required></textarea>
                                                        <button type="submit"
                                                                class="px-3 py-1.5 bg-black hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors duration-200 whitespace-nowrap border-2 border-black">
                                                            Post
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                    });
                    
                    listEl.innerHTML = questionsHtml;
                    listEl.classList.remove('hidden');
                } else {
                    // Show empty state
                    loadingEl.classList.add('hidden');
                    listEl.classList.add('hidden');
                    emptyEl.classList.remove('hidden');
                }
                
            } catch (error) {
                console.error('Error loading recent questions:', error);
                loadingEl.classList.add('hidden');
                listEl.classList.add('hidden');
                emptyEl.classList.remove('hidden');
            }
        }
        
        // Expand/collapse answer
        window.expandAnswer = function(button, id) {
            const answerEl = document.getElementById(`answer-${id}`);
            const disclaimerEl = document.getElementById(`answer-disclaimer-${id}`);
            if (answerEl.classList.contains('line-clamp-3')) {
                // Expand — remove truncation, show disclaimer
                answerEl.classList.remove('line-clamp-3');
                if (disclaimerEl) disclaimerEl.classList.remove('hidden');
                button.textContent = 'Read less';
            } else {
                // Collapse — re-add truncation, hide disclaimer
                answerEl.classList.add('line-clamp-3');
                if (disclaimerEl) disclaimerEl.classList.add('hidden');
                button.textContent = 'Read more';
            }
        };
        
        // Escape HTML to prevent XSS
        function escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }
        
        // Vote on an interaction
        window.voteInteraction = async function(interactionId, isHelpful) {
            try {
                const response = await fetch(`/api/chatgpt/interactions/${interactionId}/vote`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify({ is_helpful: isHelpful })
                });
                
                const data = await response.json();
                
                if (data.success) {
                    // Update the vote counts
                    const container = document.querySelector(`[data-interaction-id="${interactionId}"]`).closest('.bg-gray-50\\/50');
                    const helpfulCount = container.querySelector('.helpful-count');
                    const notHelpfulCount = container.querySelector('.not-helpful-count');
                    
                    helpfulCount.textContent = data.helpful_count;
                    notHelpfulCount.textContent = data.not_helpful_count;
                    
                    // Update button styles
                    const helpfulBtn = container.querySelector('[data-vote-type="helpful"]');
                    const notHelpfulBtn = container.querySelector('[data-vote-type="not-helpful"]');
                    
                    if (isHelpful) {
                        helpfulBtn.classList.remove('text-gray-600', 'hover:text-green-400');
                        helpfulBtn.classList.add('text-green-500');
                        notHelpfulBtn.classList.remove('text-red-500');
                        notHelpfulBtn.classList.add('text-gray-600', 'hover:text-red-400');
                    } else {
                        notHelpfulBtn.classList.remove('text-gray-600', 'hover:text-red-400');
                        notHelpfulBtn.classList.add('text-red-500');
                        helpfulBtn.classList.remove('text-green-500');
                        helpfulBtn.classList.add('text-gray-600', 'hover:text-green-400');
                    }
                }
            } catch (error) {
                console.error('Error voting:', error);
            }
        };
        
        // Toggle comments section
        window.toggleComments = async function(interactionId) {
            const commentsSection = document.getElementById(`comments-${interactionId}`);
            const commentsList = document.getElementById(`comments-list-${interactionId}`);
            
            if (commentsSection.classList.contains('hidden')) {
                commentsSection.classList.remove('hidden');
                
                // Load comments if not already loaded
                if (!commentsList.getAttribute('data-loaded')) {
                    try {
                                            const response = await fetch(`/api/chatgpt/interactions/${interactionId}/comments`, {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    });
                        
                        const data = await response.json();
                        
                        if (data.success) {
                            if (data.comments.length > 0) {
                                let commentsHtml = '';
                                data.comments.forEach(comment => {
                                    commentsHtml += `
                                        <div class="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                                            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                                                <span class="text-xs font-medium text-gray-700 truncate">${escapeHtml(comment.display_name)}</span>
                                                <span class="text-xs text-gray-500 whitespace-nowrap">${comment.created_at}</span>
                                            </div>
                                            <p class="text-sm text-gray-600 break-words">${escapeHtml(comment.comment)}</p>
                                        </div>
                                    `;
                                });
                                commentsList.innerHTML = commentsHtml;
                            } else {
                                commentsList.innerHTML = '<p class="text-xs text-gray-500 text-center py-2">No comments yet. Be the first!</p>';
                            }
                            commentsList.setAttribute('data-loaded', 'true');
                        }
                    } catch (error) {
                        console.error('Error loading comments:', error);
                        commentsList.innerHTML = '<p class="text-xs text-red-500 text-center py-2">Error loading comments</p>';
                    }
                }
            } else {
                commentsSection.classList.add('hidden');
            }
        };
        
        // Add comment
        window.addComment = async function(event, interactionId) {
            event.preventDefault();
            
            const form = event.target;
            const commentInput = form.querySelector('textarea[name="comment"]');
            const comment = commentInput.value.trim();
            
            if (!comment) return;
            
            const payload = { comment: comment };
            
            // Add guest fields if not authenticated
            if (!isAuthenticated) {
                const nameInput = form.querySelector('input[name="guest_name"]');
                const emailInput = form.querySelector('input[name="guest_email"]');
                payload.guest_name = nameInput.value.trim();
                payload.guest_email = emailInput.value.trim();
            }
            
            try {
                const response = await fetch(`/api/chatgpt/interactions/${interactionId}/comments`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify(payload)
                });
                
                const data = await response.json();
                
                if (data.success) {
                    // Add the new comment to the list
                    const commentsList = document.getElementById(`comments-list-${interactionId}`);
                    const newCommentHtml = `
                        <div class="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                                <span class="text-xs font-medium text-gray-700 truncate">${escapeHtml(data.comment.display_name)}</span>
                                <span class="text-xs text-gray-500 whitespace-nowrap">${data.comment.created_at}</span>
                            </div>
                            <p class="text-sm text-gray-600 break-words">${escapeHtml(data.comment.comment)}</p>
                        </div>
                    `;
                    
                    // Remove "no comments" message if it exists
                    const noCommentsMsg = commentsList.querySelector('.text-center');
                    if (noCommentsMsg) {
                        commentsList.innerHTML = '';
                    }
                    
                    // Prepend the new comment
                    commentsList.insertAdjacentHTML('afterbegin', newCommentHtml);
                    
                    // Update comment count
                    const container = form.closest('.bg-gray-50\\/50');
                    const commentCountEl = container.querySelector('.comments-count');
                    const currentCount = parseInt(commentCountEl.textContent) || 0;
                    commentCountEl.textContent = currentCount + 1;
                    
                    // Clear the input(s)
                    commentInput.value = '';
                    if (!isAuthenticated) {
                        form.querySelector('input[name="guest_name"]').value = '';
                        form.querySelector('input[name="guest_email"]').value = '';
                    }
                }
            } catch (error) {
                console.error('Error adding comment:', error);
                alert('Error adding comment. Please try again.');
            }
        };
        
        // Handle sort change
        document.getElementById('questions-sort')?.addEventListener('change', function() {
            const sort = this.value;
            const recentContent = document.getElementById('recent-questions-content');
            recentContent.setAttribute('data-loaded', 'false'); // Force reload
            loadRecentQuestions(sort);
        });
        
        // Load recent questions count on page load
        setTimeout(() => {
            const container = document.getElementById('chatgpt-qa-container');
            const categoryId = container ? container.getAttribute('data-category-id') : null;
            
            if (categoryId) {
                fetch(`${cfg.chatgpt.recentQuestionsUrl}?category_id=${categoryId}&limit=1`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    }
                })
                .then(response => response.json())
                .then(data => {
                    if (data.success && data.total_count > 0) {
                        const countEl = document.getElementById('recent-questions-count');
                        if (countEl) {
                            countEl.textContent = data.total_count > 99 ? '99+' : data.total_count;
                            countEl.classList.remove('hidden');
                        }
                    }
                })
                .catch(error => console.error('Error loading questions count:', error));
            }
        }, 1000);
    });

// Radio Button Conditional Content Handler
document.addEventListener('DOMContentLoaded', function() {
    /**
     * Enhanced function to handle conditional content visibility with support for nesting
     * @param {string} fieldName - The name of the field (radio, checkbox, or select)
     * @param {string} fieldValue - The value of the field
     * @param {Set} [processedIds=new Set()] - Set of already processed element IDs to prevent infinite recursion
     * @param {boolean} [isNestedCall=false] - Whether this is a nested call from another conditional
     */
    function handleConditionalContent(fieldName, fieldValue, processedIds = new Set(), isNestedCall = false) {
        // First, hide ALL elements that are conditionally related to this field
        document.querySelectorAll(`[data-condition-id="${fieldName}"], [data-field="${fieldName}"]`).forEach(function(element) {
            // Skip inputs themselves - only hide containers
            if (!element.matches('input, select, textarea')) {
                element.style.display = 'none';
                element.classList.remove('visible');
                element.classList.add('hidden-by-conditional');
                
                // Disable all input fields within hidden containers to prevent form submission issues
                element.querySelectorAll('input, select, textarea').forEach(input => {
                    input.disabled = true;
                });
            }
        });
        
        // Now show elements that match the current field value
        const matchingElements = [
            // Standard data-condition-id format
            ...document.querySelectorAll(`[data-condition-id="${fieldName}"][data-condition-value="${fieldValue}"]`),
            ...document.querySelectorAll(`[data-condition-id="${fieldName}"][data-condition-value="*"]`),
            
            // Alternative data-field format  
            ...document.querySelectorAll(`[data-field="${fieldName}"][data-value="${fieldValue}"]`),
            ...document.querySelectorAll(`[data-field="${fieldName}"][data-value="*"]`)
        ];
        
        matchingElements.forEach(function(element) {
            // Skip inputs themselves - only show containers
            if (!element.matches('input, select, textarea')) {
                element.style.display = 'block';
                element.classList.add('visible');
                element.classList.remove('hidden-by-conditional');
                
                // Ensure all input fields within this container are enabled and interactive
                element.querySelectorAll('input, select, textarea').forEach(input => {
                    input.disabled = false;
                    input.style.pointerEvents = 'auto';
                });
                
                // Process any nested radio buttons within this shown element
                element.querySelectorAll('input[type="radio"]:checked').forEach(nestedRadio => {
                    handleConditionalContent(nestedRadio.name, nestedRadio.value, new Set(), true);
                });
            }
        });
        
        // Handle multi-value conditions (JSON arrays)
        document.querySelectorAll(`[data-condition-id="${fieldName}"][data-condition-values], [data-field="${fieldName}"][data-condition-values]`).forEach(function(element) {
            if (element.matches('input, select, textarea')) return;
            
            const valuesAttr = element.getAttribute('data-condition-values');
            if (valuesAttr) {
                try {
                    const allowedValues = JSON.parse(valuesAttr);
                    const shouldShow = Array.isArray(allowedValues) ? 
                        allowedValues.includes(fieldValue) || allowedValues.includes('*') :
                        allowedValues === fieldValue || allowedValues === '*';
                    
                    if (shouldShow) {
                        element.style.display = 'block';
                        element.classList.add('visible');
                        element.classList.remove('hidden-by-conditional');
                        
                        // Ensure all input fields within this container are enabled and interactive
                        element.querySelectorAll('input, select, textarea').forEach(input => {
                            input.disabled = false;
                            input.style.pointerEvents = 'auto';
                        });
                        
                        // Process nested radios
                        element.querySelectorAll('input[type="radio"]:checked').forEach(nestedRadio => {
                            handleConditionalContent(nestedRadio.name, nestedRadio.value, new Set(), true);
                        });
                    }
                } catch (e) {
                    console.error('Error parsing data-condition-values:', e);
                }
            }
        });
    }
    
    // Initialize ALL radio buttons
    document.querySelectorAll('input[type="radio"]').forEach(function(radio) {
        // Add change event listener to ALL radio buttons
        radio.addEventListener('change', function() {
            if (this.checked) {
                const fieldName = this.name;
                const selectedValue = this.value;
                
                // Handle conditional content
                handleConditionalContent(fieldName, selectedValue);
            }
        });

        // Handle initial state if radio is already checked
        if (radio.checked) {
            handleConditionalContent(radio.name, radio.value);
        }
    });

    // Initialize all select elements that can trigger conditional content
    document.querySelectorAll('select').forEach(function(select) {
        // Add change event listener
        select.addEventListener('change', function() {
            const fieldName = this.name;
            const selectedValue = this.value;
            
            // Handle conditional content
            handleConditionalContent(fieldName, selectedValue);
        });

        // Trigger for initial value if one is selected
        if (select.value) {
            handleConditionalContent(select.name, select.value);
        }
    });

    // Initialize checkboxes
    document.querySelectorAll('input[type="checkbox"]').forEach(function(checkbox) {
        checkbox.addEventListener('change', function() {
            const fieldName = this.name;
            const fieldValue = this.checked ? (this.value || 'on') : 'off';
            
            handleConditionalContent(fieldName, fieldValue);
        });

        // Handle initial state
        if (checkbox.checked) {
            const fieldValue = checkbox.value || 'on';
            handleConditionalContent(checkbox.name, fieldValue);
        }
    });
});

    function downloadPdfAsGuest() {
        // Hide the auth popup
        document.getElementById('auth-popup').classList.add('hidden');

        // Get the form
        const form = document.getElementById('pdf-form');

        // Store original target
        const originalTarget = form.target;

        // Set form to submit to hidden iframe
        form.target = 'download-iframe';

        // Submit the form (downloads via iframe)
        form.submit();

        // Restore original target
        form.target = originalTarget || '';

        // Show Trustpilot modal after a short delay to allow download to start
        setTimeout(function() {
            document.getElementById('trustpilot-modal').classList.remove('hidden');
        }, 1500);
    }

    function closeTrustpilotModal() {
        document.getElementById('trustpilot-modal').classList.add('hidden');
        // Reset to rating view for next time
        setTimeout(function() {
            document.getElementById('rating-view').classList.remove('hidden');
            document.getElementById('thankyou-view').classList.add('hidden');
            resetStars();
        }, 300);
    }

    // Star rating functions
    let selectedRating = 0;

    function hoverStars(rating) {
        const stars = document.querySelectorAll('#star-rating .star-btn svg');
        stars.forEach((star, index) => {
            if (index < rating) {
                star.classList.remove('text-gray-300');
                star.classList.add('text-yellow-400');
            } else {
                star.classList.remove('text-yellow-400');
                star.classList.add('text-gray-300');
            }
        });
    }

    function resetStars() {
        if (selectedRating === 0) {
            const stars = document.querySelectorAll('#star-rating .star-btn svg');
            stars.forEach((star) => {
                star.classList.remove('text-yellow-400');
                star.classList.add('text-gray-300');
            });
        } else {
            hoverStars(selectedRating);
        }
    }

    function selectRating(rating) {
        selectedRating = rating;
        hoverStars(rating);

        // Track the rating
        if (typeof gtag !== 'undefined') {
            gtag('event', 'review_rating_selected', {
                'event_category': 'engagement',
                'event_label': cfg.name,
                'value': rating
            });
        }

        if (rating >= 4) {
            // 4 or 5 stars - redirect to Trustpilot
            window.open(cfg.trustpilotUrl, '_blank');
            closeTrustpilotModal();
        } else {
            // 1, 2, or 3 stars - show thank you message
            document.getElementById('rating-view').classList.add('hidden');
            document.getElementById('thankyou-view').classList.remove('hidden');
        }

        // Reset selected rating for next time
        selectedRating = 0;
    }

    function trackTrustpilotClick() {
        // Track the click for analytics
        if (typeof gtag !== 'undefined') {
            gtag('event', 'trustpilot_review_click', {
                'event_category': 'engagement',
                'event_label': cfg.name,
                'value': 1
            });
        }
    }

    function showTrustpilotAfterDownload(event, downloadUrl) {
        // Let the download proceed naturally (don't prevent default)
        // Show Trustpilot modal after a short delay
        setTimeout(function() {
            document.getElementById('trustpilot-modal').classList.remove('hidden');
        }, 1500);
    }

document.addEventListener('DOMContentLoaded', function () {
    const form = document.getElementById('cat-search-form');
    const input = document.getElementById('cat-search-input');
    const panel = document.getElementById('cat-search-suggestions');
    const list = document.getElementById('cat-search-results');
    const createRow = document.getElementById('cat-search-create');
    const status = document.getElementById('cat-search-status');
    const generatorUrl = cfg.generatorUrl;

    if (!form || !input || !panel || !list || !createRow || !status) {
        return;
    }

    let debounceTimer = null;
    let activeIndex = -1;
    let optionRows = [];
    let searchLogId = 0;

    // Search log: which suggestion this search led to (Enter goes through
    // click() too). Fire-and-forget; never blocks the navigation.
    const reportChoice = function (outcome, categoryId) {
        if (!searchLogId) {
            return;
        }
        try {
            const body = JSON.stringify({ id: searchLogId, category_id: categoryId || null, outcome: outcome });
            if (navigator.sendBeacon) {
                navigator.sendBeacon('/api/search/click', new Blob([body], { type: 'application/json' }));
            } else {
                fetch('/api/search/click', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: body, keepalive: true });
            }
        } catch (error) {
            // Logging must never interfere with navigation.
        }
    };

    list.addEventListener('click', function (event) {
        const row = event.target.closest('a[data-category-id]');
        if (row) {
            reportChoice('click', Number(row.getAttribute('data-category-id')));
        }
    });
    createRow.addEventListener('click', function () {
        reportChoice('generator');
    });

    const generatorSearchUrl = function (query) {
        return generatorUrl + '?search=' + encodeURIComponent(query);
    };

    const setExpanded = function (expanded) {
        input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        panel.classList.toggle('hidden', !expanded);
    };

    const closeSuggestions = function () {
        setExpanded(false);
        activeIndex = -1;
        optionRows = [];
        list.innerHTML = '';
        createRow.classList.remove('is-active', 'has-results');
        createRow.setAttribute('aria-selected', 'false');
    };

    const updateActive = function () {
        optionRows.forEach(function (row, index) {
            const active = index === activeIndex;
            row.classList.toggle('is-active', active);
            row.setAttribute('aria-selected', active ? 'true' : 'false');
        });
    };

    const renderSuggestions = function (results, query) {
        list.innerHTML = '';
        activeIndex = -1;
        optionRows = [];

        results.slice(0, 5).forEach(function (result) {
            const row = document.createElement('a');
            const name = document.createElement('span');
            const meta = document.createElement('span');

            row.href = String(result.url || '#');
            row.setAttribute('data-category-id', String(Number(result.id || 0)));
            row.className = 'cat-search-option block px-4 py-3';
            row.setAttribute('role', 'option');
            row.setAttribute('aria-selected', 'false');

            name.className = 'block text-sm font-medium text-gray-800';
            name.textContent = String(result.name || 'Legal document');

            const metaParts = [];
            if (result.type) {
                metaParts.push(String(result.type));
            }
            if (result.guided) {
                metaParts.push('Guided form');
            }
            meta.className = 'block text-xs text-gray-500 mt-0.5';
            meta.textContent = metaParts.join(' · ') || 'Template';

            row.appendChild(name);
            row.appendChild(meta);
            list.appendChild(row);
            optionRows.push(row);
        });

        const count = optionRows.length;

        createRow.href = generatorSearchUrl(query);
        createRow.classList.toggle('has-results', count > 0);
        createRow.classList.remove('is-active');
        createRow.setAttribute('aria-selected', 'false');
        optionRows.push(createRow);

        status.textContent = count > 0
            ? (count === 1 ? '1 template found' : count + ' templates found')
            : 'No matching templates — we can create this document for you';

        setExpanded(true);
    };

    const fetchSuggestions = function (query) {
        fetch('/api/forms/search?q=' + encodeURIComponent(query), {
            headers: { 'Accept': 'application/json' }
        })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Search unavailable');
                }
                return response.json();
            })
            .then(function (data) {
                if (input.value.trim() === query) {
                    searchLogId = Number(data.log_id || 0);
                    renderSuggestions(Array.isArray(data.results) ? data.results : [], query);
                }
            })
            .catch(function () {
                if (input.value.trim() === query) {
                    renderSuggestions([], query);
                }
            });
    };

    input.addEventListener('input', function () {
        window.clearTimeout(debounceTimer);
        const query = input.value.trim();

        if (query.length < 2) {
            closeSuggestions();
            status.textContent = '';
            return;
        }

        debounceTimer = window.setTimeout(function () {
            fetchSuggestions(query);
        }, 200);
    });

    input.addEventListener('keydown', function (event) {
        if (panel.classList.contains('hidden')) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            activeIndex = Math.min(activeIndex + 1, optionRows.length - 1);
            updateActive();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            activeIndex = Math.max(activeIndex - 1, -1);
            updateActive();
        } else if (event.key === 'Enter' && activeIndex >= 0 && optionRows[activeIndex]) {
            event.preventDefault();
            optionRows[activeIndex].click();
        } else if (event.key === 'Escape') {
            closeSuggestions();
        }
    });

    document.addEventListener('click', function (event) {
        if (!form.contains(event.target)) {
            closeSuggestions();
        }
    });
});

// Called from inline onclick handlers in the Blade template.
window.downloadPdfAsGuest = downloadPdfAsGuest;
window.closeTrustpilotModal = closeTrustpilotModal;
window.hoverStars = hoverStars;
window.resetStars = resetStars;
window.selectRating = selectRating;
window.trackTrustpilotClick = trackTrustpilotClick;
window.showTrustpilotAfterDownload = showTrustpilotAfterDownload;
